import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, normalizeClass as _normalizeClass, toDisplayString as _toDisplayString, createVNode as _createVNode, unref as _unref, withCtx as _withCtx, createBlock as _createBlock, createTextVNode as _createTextVNode, resolveDirective as _resolveDirective, withDirectives as _withDirectives } from "vue"

const _hoisted_1 = { class: "llm-query-event" }
const _hoisted_2 = ["innerHTML"]
const _hoisted_3 = { key: 1 }
const _hoisted_4 = { class: "flex align-items-center gap-2 w-full" }
const _hoisted_5 = { class: "font-bold white-space-nowrap" }
const _hoisted_6 = { key: 2 }
const _hoisted_7 = ["innerHTML"]
const _hoisted_8 = { key: 0 }
const _hoisted_9 = {
  class: "monospace pre",
  style: {"border":"1px var(--surface-border) solid","background-color":"var(--surface-50)","padding":"0.5rem"}
}
const _hoisted_10 = { key: 0 }
const _hoisted_11 = { key: 1 }
const _hoisted_12 = { key: 0 }
const _hoisted_13 = { class: "monospace pre" }
const _hoisted_14 = { key: 3 }
const _hoisted_15 = { key: 4 }
const _hoisted_16 = {
  key: 0,
  class: "pre"
}
const _hoisted_17 = {
  key: 1,
  class: "pre"
}
const _hoisted_18 = {
  key: 0,
  class: "pre"
}

import { inject, onBeforeMount, computed, ref, capitalize } from "vue";
import { BeakerQueryEvent, type BeakerQueryEventType, type IBeakerCell } from "beaker-kernel/src/notebook";
import { marked } from 'marked';
import BeakerCodeCell from "./BeakerCodeCell.vue";
import BeakerCodecellOutput from "./BeakerCodeCellOutput.vue";
import Accordion from "primevue/accordion";
import AccordionTab from "primevue/accordiontab";
import stripAnsi from "strip-ansi";

import { BeakerSessionComponentType } from '../session/BeakerSession.vue';
import { BeakerNotebookComponentType } from '../notebook/BeakerNotebook.vue';


// use session where possible - notebook may or may not exist, but matters for selection!
type OutputType = "stream" | "error" | "execute_result" | "display_data";



export default /*@__PURE__*/_defineComponent({
  __name: 'BeakerQueryCellEvent',
  props: [
    'event',
    'parentQueryCell',
],
  setup(__props, { expose: __expose }) {

const beakerSession = inject<BeakerSessionComponentType>("beakerSession");
const beakerNotebook = inject<BeakerNotebookComponentType>("notebook");
const codeCellRef = ref();

const props = __props;

onBeforeMount(() => {
    marked.setOptions({
    //    gfm: true,
    //    sanitize: false,
    //    langPrefix: `language-`,
     });
})

// these need to be no-ops if notebook doesn't exist in the parent UI.
const codeCellOnClick = () => {
    if (beakerNotebook) {
        beakerNotebook.selectCell(props.event?.content.cell_id)
    }
}

// see above
const parentIndex = computed(() =>
    beakerSession.session.notebook.cells.indexOf(props.parentQueryCell.value));

const isCodeCellSelected = computed(() => {
    if (beakerNotebook) {
        return parentIndex.value.toString() === beakerNotebook.selectedCellId;
    }
    return false;
});

const lastOutput = computed(() => {
    if (props.parentQueryCell?.children?.length == 0) {
        return [0]
    }
    return [props.parentQueryCell?.children?.length - 1];
})

const getCellModelById = (id): IBeakerCell | undefined => {
    const notebook = beakerSession.session.notebook;
    for (const cell of notebook.cells) {
        const target = (cell.children as IBeakerCell | undefined)?.find(
            child => child.id === id
        );
        if (typeof target !== "undefined") {
            return target;
        }
    }
    return undefined;
}

const isMarkdown = (event: BeakerQueryEvent) => {
    const markdownTypes: BeakerQueryEventType[] = ["response", "user_answer", "user_question"];
    return markdownTypes.includes(event.type);
}

// if the agent response is 'None' rather than any plain text,
// we can assume that was the result of LOOP_SUCCESS returning None,
// in which case the side effect was the main desired output,
// and the agent 'None' can safely be ignored and hidden from the user.
const isValidResponse = (event: BeakerQueryEvent) => {
    if (event.type === "response" && event.content === "None") {
        return false;
    }
    return true;
}

const markdownBody = computed(() =>
    isMarkdown(props.event) ? marked.parse(props.event.content) : "");

const formatStream = (output, shortened: boolean): string => {
    return shortened ? output.name : `stream: ${output.name}`;
}

const formatError = (output, shortened: boolean): string => {
    return shortened ? output?.ename : `${output?.ename}: ${output?.evalue}`;
}

const formatExecuteResult = (output, shortened: boolean): string => {
    const values = Object.keys(output?.data || {});
    const userFacingNames = {
        "text/plain": "Text",
        "image/png": "Image"
    };
    const result = values.sort().map(
        (format) => Object.keys(userFacingNames)
            .includes(format) ? userFacingNames[format] : format)
            .join(", ");
    return shortened ? result.split(",")[0] : result;
}

const formatOutputs = (outputs: {output_type: OutputType}[], shorten: OutputType[]): string => {
    const formatters: {[key in OutputType]: (output: object, shortened: boolean) => string} = {
        "stream": formatStream,
        "error": formatError,
        "execute_result": formatExecuteResult,
        "display_data": formatExecuteResult
    };
    const headers: string[] = outputs.map(output =>
        formatters[output.output_type](output, shorten.includes(output.output_type)));
    return headers.join(", ");
}

// get the most meaningful icon for an execute_result; e.g. plaintext is less than image/png
const executeResultIcon = (output) => {
    const outputTypeIconMap = {
        "image/png": "pi pi-chart-bar",
        "text/html": "pi pi-table",
        "text/plain": "pi pi-align-left",
        "": "pi pi-code",
    };
    const precedenceList = ["image/png", "text/html", "text/plain"];
    const values = Object.keys(output?.data || {});
    for (const desiredType of precedenceList) {
        if (values.includes(desiredType)) {
            return outputTypeIconMap[desiredType];
        }
    }
    return ""
};

const chooseOutputIcon = (outputs: {output_type: OutputType}[]) => {
    const outputTypes = outputs.map(output => output.output_type);

    const result = outputs.find(output =>
        output.output_type === "execute_result"
        || output.output_type === "display_data");
    if (result !== undefined) {
        return executeResultIcon(result);
    }

    if (outputTypes.includes("error")) {
        return "pi pi-times-circle"
    }

    return "pi pi-pen-to-square"
}

function execute() {
    //const future = props.cell.execute(session);
}

__expose({
    execute,
});


return (_ctx: any,_cache: any) => {
  const _directive_keybindings = _resolveDirective("keybindings")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (isMarkdown(__props.event) && isValidResponse(__props.event))
      ? (_openBlock(), _createElementBlock("span", {
          key: 0,
          innerHTML: markdownBody.value
        }, null, 8, _hoisted_2))
      : _createCommentVNode("", true),
    (props.event?.type === 'response' && __props.parentQueryCell?.children?.length !== 0)
      ? (_openBlock(), _createElementBlock("span", _hoisted_3, [
          _cache[0] || (_cache[0] = _createElementVNode("h4", { class: "agent-outputs" }, "Outputs:", -1)),
          _createVNode(_unref(Accordion), {
            multiple: true,
            "active-index": lastOutput.value
          }, {
            default: _withCtx(() => [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(__props.parentQueryCell?.children?.entries(), ([index, child]) => {
                return (_openBlock(), _createBlock(_unref(AccordionTab), {
                  key: index,
                  pt: {
                        header: {
                            class: [`agent-response-header`]
                        },
                        headerAction: {
                            class: [`agent-response-headeraction`]
                        },
                        content: {
                            class: [`agent-response-content`]
                        },
                        headerIcon: {
                            class: [`agent-response-icon`]
                        }
                    }
                }, {
                  header: _withCtx(() => [
                    _createElementVNode("span", _hoisted_4, [
                      _createElementVNode("span", {
                        class: _normalizeClass(chooseOutputIcon(child?.outputs))
                      }, null, 2),
                      _createElementVNode("span", _hoisted_5, _toDisplayString(formatOutputs(child?.outputs, ["execute_result"])), 1)
                    ])
                  ]),
                  default: _withCtx(() => [
                    _createVNode(BeakerCodecellOutput, {
                      outputs: child?.outputs
                    }, null, 8, ["outputs"])
                  ]),
                  _: 2
                }, 1024))
              }), 128))
            ]),
            _: 1
          }, 8, ["active-index"])
        ]))
      : (props.event?.type === 'thought')
        ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
            _createElementVNode("div", {
              innerHTML: _unref(marked).parse(props.event.content.thought)
            }, null, 8, _hoisted_7),
            (props.event.content.background_code_executions.length)
              ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
                  _createVNode(_unref(Accordion), {
                    multiple: true,
                    "active-index": []
                  }, {
                    default: _withCtx(() => [
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(__props.event.content.background_code_executions, (code_execution, index) => {
                        return (_openBlock(), _createBlock(_unref(AccordionTab), {
                          key: `code_execution_${index}`,
                          pt: {
                            header: {
                                class: [`agent-response-header`]
                            },
                            headerAction: {
                                class: [`agent-response-headeraction`]
                            },
                            content: {
                                class: [`agent-response-content`]
                            },
                            headerIcon: {
                                class: [`agent-response-icon`]
                            }
                        }
                        }, {
                          header: _withCtx(() => _cache[1] || (_cache[1] = [
                            _createElementVNode("span", {
                              class: "flex align-items-center gap-2 w-full",
                              style: {"font-weight":"normal"}
                            }, [
                              _createElementVNode("span", { class: "pi pi-icon pi-server" }),
                              _createElementVNode("span", null, "Background Execution by Agent")
                            ], -1)
                          ])),
                          default: _withCtx(() => [
                            _createElementVNode("div", _hoisted_9, _toDisplayString(code_execution.code.trim()), 1),
                            (code_execution.status === 'ok')
                              ? (_openBlock(), _createElementBlock("div", _hoisted_10, _cache[2] || (_cache[2] = [
                                  _createElementVNode("span", { class: "pi pi-icon pi-check" }, null, -1),
                                  _createTextVNode(" Ran successfully ")
                                ])))
                              : (_openBlock(), _createElementBlock("div", _hoisted_11, [
                                  _cache[3] || (_cache[3] = _createElementVNode("span", { class: "pi pi-icon pi-exclamation-triangle" }, null, -1)),
                                  _createTextVNode(" " + _toDisplayString(capitalize(code_execution.status)) + " ", 1),
                                  (code_execution.status === 'error')
                                    ? (_openBlock(), _createElementBlock("div", _hoisted_12, [
                                        _createElementVNode("div", null, _toDisplayString(code_execution.ename) + ": " + _toDisplayString(code_execution.evalue), 1),
                                        _createElementVNode("div", _hoisted_13, _toDisplayString(_unref(stripAnsi)(code_execution.traceback.join('\n').trim())), 1)
                                      ]))
                                    : _createCommentVNode("", true)
                                ]))
                          ]),
                          _: 2
                        }, 1024))
                      }), 128))
                    ]),
                    _: 1
                  })
                ]))
              : _createCommentVNode("", true)
          ]))
        : (props.event?.type === 'code_cell')
          ? (_openBlock(), _createElementBlock("span", _hoisted_14, [
              _withDirectives(_createVNode(BeakerCodeCell, {
                onClick: codeCellOnClick,
                cell: getCellModelById(props?.event.content.cell_id),
                "drag-enabled": false,
                class: _normalizeClass({
                    selected: isCodeCellSelected.value,
                    'query-event-code-cell': true
                }),
                "hide-output": true,
                ref_key: "codeCellRef",
                ref: codeCellRef
              }, null, 8, ["cell", "class"]), [
                [_directive_keybindings, {
                    'keydown.enter.ctrl.prevent.capture.in-editor': (evt) => {
                        codeCellRef.value.execute();
                    },
                    'keydown.enter.shift.prevent.capture.in-editor': (evt) => {
                        codeCellRef.value.execute();
                    }
                }]
              ]),
              _cache[4] || (_cache[4] = _createElementVNode("span", { class: "output-hide-text" }, "(Output hidden -- shown in full response below.)", -1))
            ]))
          : (props.event?.type === 'error')
            ? (_openBlock(), _createElementBlock("span", _hoisted_15, [
                _createElementVNode("div", null, [
                  (props?.event.content.ename)
                    ? (_openBlock(), _createElementBlock("pre", _hoisted_16, "                    " + _toDisplayString(props?.event.content.ename) + "\n                ", 1))
                    : _createCommentVNode("", true),
                  (props?.event.content.evalue)
                    ? (_openBlock(), _createElementBlock("pre", _hoisted_17, "                    " + _toDisplayString(props?.event.content.evalue) + "\n                ", 1))
                    : _createCommentVNode("", true),
                  _createVNode(_unref(Accordion), null, {
                    default: _withCtx(() => [
                      _createVNode(_unref(AccordionTab), { pt: {
                            header: {
                                class: [`agent-response-header`]
                            },
                            headerAction: {
                                class: [`agent-response-headeraction`]
                            },
                            content: {
                                class: [`agent-response-content`, 'agent-response-content-error']
                            },
                            headerIcon: {
                                class: [`agent-response-icon`]
                            }
                        } }, {
                        header: _withCtx(() => _cache[5] || (_cache[5] = [
                          _createElementVNode("span", { class: "flex align-items-center gap-2 w-full" }, [
                            _createElementVNode("span", { class: "font-bold white-space-nowrap" }, "Traceback:")
                          ], -1)
                        ])),
                        default: _withCtx(() => [
                          (props?.event.content.traceback)
                            ? (_openBlock(), _createElementBlock("pre", _hoisted_18, "                            " + _toDisplayString(props?.event.content.traceback?.join('\n')) + "\n                        ", 1))
                            : _createCommentVNode("", true)
                        ]),
                        _: 1
                      })
                    ]),
                    _: 1
                  })
                ])
              ]))
            : _createCommentVNode("", true)
  ]))
}
}

})